<template>
  <BaseModal
    :card-width="'560px'"
    no-toolbar-padding
    no-padding
    no-toolbar
    no-exit
    @close="$emit('close')"
  >
    <template #default>
      <div class="relative w-full flex items-start gap-2 border-b border-border-normal p-5">
        <img
          src="../../assets/icons/spyder-token.png"
          class="w-5 h-5"
        >
        <div class="space-y-1">
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Get Additional Spyder Credits
          </BaseText>
          <BaseText
            type="body"
            size="sm"
            class="text-text-normal"
          >
            Track and store more competitor data.
          </BaseText>
        </div>
        <button
          class="absolute top-3 right-3 p-1"
          @click="$emit('close')"
        >
          <TagRemoveIcon
            stroke="#5E6678"
            :width="16"
            :height="16"
          />
        </button>
      </div>
      <div class="p-5 space-y-5 border-b border-border-normal">
        <div
          v-if="!isWithinLimit"
          class="p-3 rounded-lg bg-secondary-red-10 flex items-center gap-1"
        >
          <WarningIcon class="text-secondary-red-300 w-5 h-5" />
          <BaseText
            type="label"
            size="sm"
            class="text-secondary-red-300"
          >
            Too many subscribed brands. Remove {{ currentSubCount - selectedPackage.brandCount }} brands to update.
          </BaseText>
        </div>
        <div
          v-else
          class="p-3 rounded-lg bg-background-normal flex items-center justify-between"
        >
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            {{ spyderTitleText }}
          </BaseText>
          <button
            class="flex items-center gap-1.5"
          >
            <img
              v-if="currentSubCount > 0"
              src="../../assets/icons/spyder-token.png"
              class="w-5 h-5"
            >
            <img
              v-else
              src="../../assets/icons/spyder-token-disabled.png"
              class="w-5 h-5"
            >
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              {{ currentSubCount }}/{{ getSpyderLimit }} credits
            </BaseText>
          </button>
        </div>
        <!-- Packages -->
        <div class="space-y-3">
          <button
            v-for="(spyderPackage) in SPYDER_PACKAGES"
            :key="`package-${spyderPackage.key}`"
            class="p-3 flex items-start gap-3 w-full border border-border-normal rounded-xl relative disabled:cursor-not-allowed"
            :disabled="currentPackage?.key === spyderPackage?.key"
            @click="selectedPackage = spyderPackage"
          >
            <div
              v-if="currentPackage?.key === spyderPackage?.key"
              class="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-opacity-50 bg-white rounded-xl"
            >
              <BaseText
                type="label"
                size="sm"
                class="text-text-normal"
              >
                Current Package
              </BaseText>
            </div>
            <div
              class="radio-button mt-0.5 flex-shrink-0"
              :class="{'selected': selectedPackage?.key === spyderPackage?.key}"
            >
              <div class="radio-button-inner" />
            </div>
            <div class="space-y-2 w-full">
              <div class="flex items-start gap-1">
                <img
                  v-if="currentSubCount > 0"
                  src="../../assets/icons/spyder-token.png"
                  class="w-5 h-5"
                >
                <BaseText
                  type="label"
                  size="sm"
                  class="text-text-muted ml-1"
                >
                  {{ spyderPackage.brandCount }}
                </BaseText>
                <BaseText
                  size="sm"
                  class="text-text-normal"
                >
                  Brands
                </BaseText>
              </div>
              <BaseText
                type="label"
                size="sm"
                class="text-left w-max"
              >
                <span class="text-text-muted">${{ spyderPackage.monthlyPrice }}</span>
                <span class="text-text-normal">/month</span>
              </BaseText>
            </div>
            <div class="flex-shrink-0 space-y-2">
              <BaseText
                size="sm"
              >
                <span class="text-text-muted">${{ (spyderPackage.monthlyPrice / spyderPackage.brandCount).toFixed(2) }}</span>
                <span class="text-text-normal">/brand</span>
              </BaseText>
              <div
                class="rounded-full px-2.5 py-1"
                :class="spyderPackage.savingsPercent === 0 ? 'border border-border-normal' : 'bg-primary-green-10 text-primary-green-200'"
              >
                <BaseText size="sm">
                  Save ~{{ spyderPackage.savingsPercent }}%
                </BaseText>
              </div>
            </div>
          </button>
        </div>
        <!--  -->
      </div>
      <div class="p-5 space-y-4">
        <div class="bg-secondary-green-10 rounded-lg p-2 flex items-center justify-start">
          <div class="grid grid-cols-12">
            <div class="col-span-1 p-1.5">
              <InfiniteIcon class="text-secondary-green-200 w-5 h-5" />
            </div>
            <div class="col-span-8 space-y-1 p-1.5">
              <BaseText
                type="label"
                size="sm"
                class="text-secondary-green-300 font-medium"
              >
                Get Unlimited Spyder Credits!
              </BaseText>
              <BaseText
                size="sm"
                class="text-secondary-green-300"
              >
                Instead of buying an additional credit pack, change to annual billing to get unlimited Spyder credits on your current plan.
              </BaseText>
            </div>
            <div class="col-span-3">
              <button
                class="flex gap-1.5 items-center py-1.5 pl-2 pr-2.5 hover:bg-secondary-green-25 transition-colors rounded-md"
                @click="showSubscriptionDrawer"
              >
                <UpgradeIcon class="text-secondary-green-300 w-5 h-5" />
                <BaseText
                  type="label"
                  size="sm"
                  class="text-secondary-green-300 whitespace-nowrap"
                >
                  Change Plan
                </BaseText>
              </button>
            </div>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <button
            class="base-btn py-1.5 px-2.5"
            @click="$emit('close')"
          >
            <BaseText
              type="label"
              size="sm"
            >
              Cancel
            </BaseText>
          </button>
          <BaseButton
            primary
            class="ml-auto"
            :disabled="!selectedPackage || purchaseLoading || !isWithinLimit"
            :loading="purchaseLoading"
            @click="purchasePackage"
          >
            Confirm
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { startCase } from 'lodash'
import { SPYDER_PACKAGES, SUBSCRIPTION_PLANS } from '@/utils/stripe/productMeta'
// Icons
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import UpgradeIcon from '../globals/Icons/SidebarIcons/UpgradeIcon.vue'
import InfiniteIcon from '../globals/Icons/InfiniteIcon.vue'
import WarningIcon from '../globals/Icons/WarningIcon.vue'

export default {
  name: 'BuySpyderCreditsModal',
  components: {
    TagRemoveIcon,
    UpgradeIcon,
    InfiniteIcon,
    WarningIcon
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getTeam', 'getStripeCustomer', 'getUserSubscription', 'getUserFreeTrialDate', 'isFreeTier', 'getTeamSubscriptionState', 'getLensLimit', 'getSpyderLimit']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions']),
    currentSubCount () {
      return this.getSpyderSubscriptions?.length
    },
    isWithinLimit () {
      if (!this.selectedPackage) return true
      return this.currentSubCount <= this.selectedPackage.brandCount
    },
    spyderTitleText () {
      const isSpyderFromBasePlan = this.getUserSubscription.addOns.spyder?.isFromBasePlan
      const planName = startCase(this.getUserSubscription?.planName?.replace(/_/g, ' '))
      if (isSpyderFromBasePlan) {
        return this.isFreeTier ? 'You are currently on a free-trial.' : `${planName}, ${this.getSpyderLimit} included credits.`
      }

      return `${planName}, ${this.getSpyderLimit} total credits.`
    },
    activeSpyderPrice () {
      return this.getUserSubscription.addOns.spyder?.priceId
    },
    currentPackage () {
      return this.getUserSubscription.addOns.spyder?.isFromBasePlan ? null : this.SPYDER_PACKAGES.find(p => p.priceId.monthly === this.activeSpyderPrice)
    }
  },
  data () {
    return {
      SPYDER_PACKAGES,
      SUBSCRIPTION_PLANS,
      selectedPackage: null,
      purchaseLoading: false
    }
  },
  methods: {
    ...mapMutations('MiscModule', ['SET_SUBSCRIPTION_DRAWER_VIEW_MODE']),
    ...mapMutations('AuthModule', ['SET_APP_LIMITS', 'SET_USER_SUBSCRIPTION']),
    ...mapActions('SpyderModule', ['fetchSpyderLimit']),
    ...mapActions('AuthModule', ['setUserSubscription', 'fetchUserSubscription']),
    showSubscriptionDrawer () {
      this.$emit('close')
      this.SET_SUBSCRIPTION_DRAWER_VIEW_MODE({ state: true, initInterval: 'annual' })
    },
    async refreshLocalSubscription (update) {
      const { quantity, priceId, productId } = update || {}
      const baseSpyderLimit = SUBSCRIPTION_PLANS.find(p => p.key === this.getUserSubscription?.internalProductId)?.baseSpyder?.monthly ?? 0
      this.SET_APP_LIMITS({ spyder: baseSpyderLimit + quantity, lens: this.getLensLimit })
      const subUpdate = {
        ...this.getUserSubscription,
        addOns: {
          ...this.getUserSubscription.addOns,
          spyder: {
            isFromBasePlan: false,
            quantity,
            priceId,
            productId
          }
        }
      }
      this.SET_USER_SUBSCRIPTION(subUpdate)
    },
    async purchasePackage () {
      this.purchaseLoading = true
      try {
        const { data } = await ForeplayAPI.Subscription.createSpyderSubscription({ priceId: this.selectedPackage.priceId.monthly })
        this.refreshLocalSubscription(data)
        this.$showAlert({ type: 'success', message: 'Your package has been purchased successfully' })
        this.$emit('close')
      } catch (error) {
        console.error(error)
        const errorMessage = error.response.data.message || 'Something went wrong purchasing the package. Please try again later.'
        this.$showAlert({ type: 'error', message: errorMessage })
      } finally {
        this.purchaseLoading = false
      }
    },
    showIntercom () {
      window.Intercom('show')
    }
  }
}
</script>

<style scoped>
.radio-button {
  position: relative;
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(4, 26, 75, 0.13);
  background-color: white;
  transition: box-shadow 150ms ease-in-out, background-color 150ms ease-in-out;
}
.radio-button.selected {
  box-shadow: none;
  background-color: #ED615A;
}
.radio-button-inner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(128, 30, 29, 0.60);
  opacity: 0;
  transition: opacity 150ms ease-in-out;
}
.radio-button.selected .radio-button-inner {
  opacity: 1;
}
</style>
